import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error = false,
      helperText,
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = id || generatedId;

    const checkboxClasses = [
      'accent-black',
      'border-gray-300',
      'rounded',
      'focus:ring-black',
      'focus:ring-2',
      'focus:ring-offset-2',
      'transition-all',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      error ? 'border-red-500' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // If no label, return just the checkbox
    if (!label) {
      return (
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className={checkboxClasses}
          disabled={disabled}
          {...props}
        />
      );
    }

    // With label, return label wrapper
    return (
      <div className="inline-flex flex-col">
        <label
          htmlFor={checkboxId}
          className={`inline-flex items-center space-x-2 ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
        >
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            className={checkboxClasses}
            disabled={disabled}
            {...props}
          />
          <span className="text-sm select-none">{label}</span>
        </label>
        {helperText && (
          <span
            className={`text-xs mt-1 ml-6 ${error ? 'text-red-600' : 'text-gray-600'}`}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
